import type { SelectOption } from '~/shared/utils/types';

import type { ViewMode } from '../../types/exploration';
import {
  eraseCodec,
  selectOptionCodec,
  stringListCodec,
  tagOptionCodec,
  type ErasedValueCodec,
} from './dimensionCodecs';
import type { ExplorationFilterPanelState } from './utils/filterConverters';
import type { SectionId, SectionOptions } from './utils/sectionVisibility';

/** Panel-state keys whose value is a toggleable list (i.e. everything except capacity). */
export type ListStateKey = {
  [K in keyof ExplorationFilterPanelState]: ExplorationFilterPanelState[K] extends
    | string[]
    | SelectOption[]
    ? K
    : never;
}[keyof ExplorationFilterPanelState];

export interface CheckboxDimension {
  kind: 'checkbox';
  id: Exclude<SectionId, 'capacity' | 'support'>;
  title: string;
  stateKey: ListStateKey;
  codec: ErasedValueCodec;
  /** Which `SectionOptions` entry feeds this section's checkbox list. */
  optionsKey: keyof SectionOptions;
  /** Passes the panel's global query down, and gates the search-driven auto-open. */
  respondsToGlobalSearch: boolean;
  showSectionSearch?: boolean;
  tagVariant?: 'cluster' | 'instance' | 'volume' | 'node' | 'default';
  showCount?: boolean;
}

/** Sections with bespoke bodies that still reuse the shared section shell. */
export interface CustomDimension {
  kind: 'custom';
  id: 'support' | 'capacity';
  title: string;
  respondsToGlobalSearch: false;
}

export type ExplorationDimension = CheckboxDimension | CustomDimension;

/**
 * The filter panel's sections, in render order.
 *
 * This order is load-bearing and was previously implicit in 395 lines of JSX — note that
 * `diskType` renders LAST, after capacity, despite sitting mid-list in the visibility map.
 *
 * `accountId` is deliberately absent: it is a headless dimension. It round-trips through
 * `filterConverters` so already-applied account filters survive an apply, but it renders no
 * section. See `filterConverters.test.ts`.
 */
export const explorationDimensions: readonly ExplorationDimension[] = [
  {
    kind: 'checkbox',
    id: 'activation',
    title: 'Activation',
    stateKey: 'activationStatuses',
    codec: eraseCodec(stringListCodec),
    optionsKey: 'activation',
    respondsToGlobalSearch: false,
  },
  { kind: 'custom', id: 'support', title: 'Eligibility', respondsToGlobalSearch: false },
  {
    kind: 'checkbox',
    id: 'infrastructure',
    title: 'Attached To',
    stateKey: 'infrastructureFilters',
    codec: eraseCodec(stringListCodec),
    optionsKey: 'infrastructure',
    respondsToGlobalSearch: false,
  },
  {
    kind: 'checkbox',
    id: 'region',
    title: 'Region',
    stateKey: 'regionFilters',
    codec: eraseCodec(stringListCodec),
    optionsKey: 'region',
    respondsToGlobalSearch: false,
  },
  {
    kind: 'checkbox',
    id: 'cluster',
    title: 'Cluster',
    stateKey: 'clusterFilters',
    codec: eraseCodec(selectOptionCodec('clusterName')),
    optionsKey: 'cluster',
    respondsToGlobalSearch: true,
    showSectionSearch: true,
    tagVariant: 'cluster',
  },
  {
    kind: 'checkbox',
    id: 'nodeGroup',
    title: 'Node Group',
    stateKey: 'nodeGroupFilters',
    codec: eraseCodec(stringListCodec),
    optionsKey: 'nodeGroup',
    respondsToGlobalSearch: true,
    showSectionSearch: true,
    tagVariant: 'node',
  },
  {
    kind: 'checkbox',
    id: 'instanceId',
    title: 'Instance ID',
    stateKey: 'instanceFilters',
    codec: eraseCodec(selectOptionCodec('instanceId')),
    optionsKey: 'instanceId',
    respondsToGlobalSearch: true,
    showSectionSearch: true,
    tagVariant: 'instance',
  },
  {
    kind: 'checkbox',
    id: 'volumeId',
    title: 'Volume ID',
    stateKey: 'volumeIdFilters',
    codec: eraseCodec(selectOptionCodec('volumeId')),
    optionsKey: 'volumeId',
    respondsToGlobalSearch: true,
    showSectionSearch: true,
    showCount: false,
  },
  {
    kind: 'checkbox',
    id: 'tags',
    title: 'Volume Tags',
    stateKey: 'tagFilters',
    codec: eraseCodec(tagOptionCodec),
    optionsKey: 'tags',
    respondsToGlobalSearch: true,
    showSectionSearch: true,
    tagVariant: 'volume',
  },
  { kind: 'custom', id: 'capacity', title: 'Capacity', respondsToGlobalSearch: false },
  {
    kind: 'checkbox',
    id: 'diskType',
    title: 'Volume Type',
    stateKey: 'diskTypeFilters',
    codec: eraseCodec(stringListCodec),
    optionsKey: 'diskType',
    respondsToGlobalSearch: false,
  },
];

export const dimensionDefaultOpen: Record<SectionId, boolean> = {
  activation: true,
  support: true,
  infrastructure: true,
  cluster: false,
  region: false,
  nodeGroup: false,
  diskType: false,
  volumeId: false,
  instanceId: false,
  tags: false,
  capacity: false,
};
